import React from "react";
import NoData, { hasData } from "./NoData";
import { BRAND, NEUTRAL } from "../../utils/chartTheme";

const formatCompact = (num) => {
  const n = Number(num) || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString();
};

const shareOf = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

const LegendSide = ({ color, count, label, percent }) => (
  <div className="flex items-start gap-2 text-sm text-left min-w-[80px]">
    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
    <div className="flex flex-col leading-tight gap-0.5">
      <span className="font-bold text-gray-800">{Number(count || 0).toLocaleString()}</span>
      <span className="font-semibold text-gray-700">{label}</span>
      <span className="font-semibold text-gray-800">{percent}%</span>
    </div>
  </div>
);

export const YesNoDonut = ({ yes, no, yesLabel, noLabel, total }) => {
  const yesCount = Number(yes) || 0;
  const noCount = Number(no) || 0;
  if (!hasData(yesCount, noCount)) return <NoData />;
  const sum = total || yesCount + noCount;
  const yesPct = shareOf(yesCount, yesCount + noCount);
  const noPct = shareOf(noCount, yesCount + noCount);

  const radius = 40;
  const stroke = 8;
  const center = radius + stroke;
  const circumference = 2 * Math.PI * radius;
  const yesLength = (yesCount / (yesCount + noCount)) * circumference;

  return (
    <div className="flex items-center justify-center gap-4 py-2">
      <LegendSide color={BRAND.teal} count={yesCount} label={yesLabel} percent={yesPct} />
      <div className="relative flex-shrink-0" style={{ width: center * 2, height: center * 2 }}>
        <svg width={center * 2} height={center * 2} className="-rotate-90">
          <circle cx={center} cy={center} r={radius} stroke={NEUTRAL.track} strokeWidth={stroke} fill="transparent" />
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={BRAND.gold}
            strokeWidth={stroke}
            fill="transparent"
            strokeDasharray={`${circumference - yesLength} ${circumference}`}
            strokeDashoffset={-yesLength}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={BRAND.teal}
            strokeWidth={stroke}
            fill="transparent"
            strokeDasharray={`${yesLength} ${circumference}`}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-bold text-gray-800">{Number(sum).toLocaleString()}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
      </div>
      <LegendSide color={BRAND.gold} count={noCount} label={noLabel} percent={noPct} />
    </div>
  );
};

const IconCard = ({ icon, color, label, count, percent }) => (
  <div className="text-center">
    <div
      className="rounded-2xl text-white text-xl w-9 h-9 mx-auto my-1 flex items-center justify-center"
      style={{ backgroundColor: color }}
    >
      {icon}
    </div>

    <h5 className="text-sm text-gray-700 font-semibold leading-tight min-h-[2.5rem] flex items-center justify-center">
      {label}
    </h5>
    <h4 className="text-xl m-0 p-0 text-black font-bold" title={Number(count || 0).toLocaleString()}>
      {formatCompact(count)} <span className="text-sm text-gray-600 font-medium">({percent}%)</span>
    </h4>
  </div>
);

export const YesNoCards = ({ yes, no, yesLabel, noLabel }) => {
  const yesCount = Number(yes) || 0;
  const noCount = Number(no) || 0;
  if (!hasData(yesCount, noCount)) return <NoData />;
  const total = yesCount + noCount;
  const pct = (v) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0.0");
  return (
    <div className="grid grid-cols-2 gap-4 py-2">
      <IconCard icon="✓" color={BRAND.teal} label={yesLabel} count={yesCount} percent={pct(yesCount)} />
      <IconCard icon="✗" color={BRAND.gold} label={noLabel} count={noCount} percent={pct(noCount)} />
    </div>
  );
};
